"use client";

import { useTranslations } from "next-intl";
import { BsFileImage, BsFilm } from "react-icons/bs";
import { generateIcon, ShowGalleryStatusEnum } from "utils";
import { useMediaContext } from "../../context";
import { BooleanStatus } from "../boolean-status";
import { MediaEnum } from "./enums";
import type { FilterMediaByType, PhotoItemType, VideoItemType } from "./types";
import { filterItems } from "./utils";
import { useSearchParams } from "next/navigation";
import { type Dispatch, type SetStateAction, useMemo } from "react";

type FilterStateType = {
  video: boolean;
  photo: boolean;
};
interface IMediaFilterProps {
  items: (VideoItemType | PhotoItemType)[];
  filterBy: FilterMediaByType;
  setFilterBy: Dispatch<SetStateAction<FilterMediaByType>>;
  showFilter: boolean;
  isSlideViewActive?: boolean;
  setIsSlideViewActive?: Dispatch<SetStateAction<boolean>>;
  slideViewImageCount?: number;
  setSlideViewImageCount?: Dispatch<SetStateAction<number>>;
}

export default function MediaFilter({
  items,
  filterBy,
  setFilterBy,
  showFilter,
  isSlideViewActive = false,
  setIsSlideViewActive,
  slideViewImageCount = 4,
  setSlideViewImageCount,
}: IMediaFilterProps) {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const galleryFilterStatus = searchParams.get("f");

  const { selectedItems, selectItems } = useMediaContext();

  const handleChangeFilter = (type: MediaEnum, value: boolean) => {
    const newFilterBy = { ...filterBy, [type]: value };
    const filteredItems = filterItems(items, newFilterBy, showFilter);

    selectItems(
      selectedItems.filter((id: string) => {
        return filteredItems.some((filteredItem) => filteredItem.id === id);
      })
    );

    setFilterBy(newFilterBy);
  };
  const getIsActive = (type: MediaEnum) => {
    return filterBy[type];
  };

  const getFilterStateByStatus = (
    status: ShowGalleryStatusEnum
  ): FilterStateType => {
    const DEFAULT_FILTER_STATE: FilterStateType = { video: true, photo: true };
    const PHOTO_ONLY_STATE: FilterStateType = { video: false, photo: true };
    const NO_MEDIA_STATE: FilterStateType = { video: false, photo: false };

    switch (status) {
      case ShowGalleryStatusEnum.no:
        return NO_MEDIA_STATE;
      case ShowGalleryStatusEnum.onlyPhoto:
        return PHOTO_ONLY_STATE;
      case ShowGalleryStatusEnum.photoAndVideo:
        return DEFAULT_FILTER_STATE;
      default:
        return DEFAULT_FILTER_STATE;
    }
  };

  const showFilterWhen = useMemo(() => {
    return getFilterStateByStatus(
      galleryFilterStatus
        ? (Number(galleryFilterStatus) as ShowGalleryStatusEnum)
        : undefined
    );
  }, [galleryFilterStatus]);

  if (!showFilterWhen.video && !showFilterWhen.photo) {
    return null;
  }
  return (
    <div className="flex items-center text-sm rounded-md">
      <div className="mr-4">{t("Gallery.filter")}:</div>
      {showFilterWhen.photo ? (
        <div
          className="label flex items-center cursor-pointer"
          onClick={() =>
            handleChangeFilter(MediaEnum.Photo, !filterBy[MediaEnum.Photo])
          }
        >
          <div className="mr-2">
            {generateIcon(
              BsFileImage,
              `transition text-[20px] ${
                getIsActive(MediaEnum.Photo)
                  ? "text-purple-700/75 group-hover:text-purple-700"
                  : "text-gray-400"
              }`
            )}
          </div>
          <BooleanStatus value={getIsActive(MediaEnum.Photo)} />
        </div>
      ) : null}
      {showFilterWhen.video ? (
        <div
          className="label flex items-center cursor-pointer ml-4"
          onClick={() =>
            handleChangeFilter(MediaEnum.Video, !filterBy[MediaEnum.Video])
          }
        >
          <div className="mr-2">
            {generateIcon(
              BsFilm,
              `transition text-[20px] ${
                getIsActive(MediaEnum.Video)
                  ? "text-purple-700/75 group-hover:text-purple-700"
                  : "text-gray-400"
              }`
            )}
          </div>
          <BooleanStatus value={getIsActive(MediaEnum.Video)} />
        </div>
      ) : null}
      
       {setIsSlideViewActive && setSlideViewImageCount ?  (
        <div className="ml-6 flex items-center gap-4">
          <label className="label flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-purple-700"
              checked={isSlideViewActive}
              onChange={(e) => setIsSlideViewActive(e.target.checked)}
            />
            <span className="ml-2">{t("Gallery.slideView")}</span>
          </label>
          
          <div className="flex items-center">
            <label className="mr-2">{t("Gallery.imageCount")}:</label>
            <input
              type="number"
              min={1}
              max={100}
              value={slideViewImageCount}
              onChange={(e) => {
                const value = Math.min(100, Math.max(1, parseInt(e.target.value) || 1));
                setSlideViewImageCount(value);
              }}
              className="input input-sm input-bordered w-20"
            />
          </div>
        </div>
      ) : null}    </div>
  );
}
